import React from "react";
import axios from "axios";
import {
  Row,
  Container,
  Col,
  Nav,
  Tab,
  ButtonGroup,
  Button,
  Spinner,
  ListGroup,
} from "react-bootstrap";
import BootstrapTable from "react-bootstrap-table-next";
import paginationFactory from "react-bootstrap-table2-paginator";
import { t } from "../../localization/localization";
import { PrintButton } from "../../components/printButton";

import profilePlaceholder from "../../assets/profile_ph.jpg";
import "./profile.css";

import DashboardSummary from "./profile/dashboardSummary";
import DashboardExternalScopus from "./profile/dashboardExternalScopus";
import DashboardExternalScholar from "./profile/dashboardExternalScholar";
import DashboardExternalOpenAlex from "./profile/dashboardOpenAlex";
import DashboardExternalSemanticScholar from "./profile/dashboardExternalSemanticScholar";
import DashboardSdgRosettes from "./profile/dashboardSdgRosettes";
import { getGoogleAnalyticsReportByCountry } from "../../api/google-analytics/google-analytics.api";
import DashboardGoogleAnalyticsMap from "./profile/dashboardGoogleAnalyticsMap";
import DashboardExternalWos from "./profile/dashboardExternalWos";

const options = {
  sizePerPage: 10,
  hideSizePerPage: true,
  hidePageListOnlyOnePage: true,
};

const PUBLICATION_TYPES = (locale) => [
  {
    value: "ARTICLE",
    text: t(locale, "/rs/profile/publicationTypes/articles"),
  },
  {
    value: "CONFERENCE",
    text: t(locale, "/rs/profile/publicationTypes/conferences"),
  },
  {
    value: "BOOK",
    text: t(locale, "/rs/profile/publicationTypes/bookChapters"),
  },
  {
    value: "OTHER",
    text: t(locale, "/rs/profile/publicationTypes/others"),
  },
];

export default class Profile extends React.Component {
  constructor(props) {
    super(props);
    this.crisId = this.getCrisId();
    this.showAnalyticsMap = window._env_.DISPLAY_ANALYTICS_MAP;

    this.state = {
      imageURL: "",
      cvURL: "",
      title: "",
      variants: "",
      mainAffiliation: "",
      email: "",
      status: "",
      otherEmails: "",
      country: "",
      orcid: "",
      scopusAuthorId: "",
      researcherId: "",
      googleScholar: "",
      affiliations: [],
      educations: [],
      biography: "",
      biographyNative: "",
      yokLink: "",
      websiteLink: "",
      websiteDesc: "",
      researchInterests: "",
      awards: [],
      publicationType: PUBLICATION_TYPES("tr")[0],
      publications: {
        ARTICLE: [],
        CONFERENCE: [],
        BOOK: [],
        OTHER: [],
      },
      theses: [],
      projects: [],
      analyticsData: [],
      isAnalyticsDataLoading: true,
    };
  }

  getCrisId = () => {
    const urlSearchParams = new URLSearchParams(this.props.location.search);
    return urlSearchParams.get("crisId");
  };

  componentDidMount() {
    const urlSearchParams = new URLSearchParams(this.props.location.search);
    const locale = urlSearchParams.get("locale") ?? "tr";

    this.id = parseInt(this.crisId.slice(2), 10);
    var analytics = [];
    let date = new Date();
    getGoogleAnalyticsReportByCountry(
      window._env_.GOOGLE_ANALYTICS_START_YEAR,
      date.toISOString().split("T")[0],
      `/cris/rp/${this.getCrisId()}`
    )
      .then((response) => {
        if (response.rows) {
          analytics = response.rows.map((element) => {
            return { name: element[1], value: element[2] };
          });
        }
      })
      .catch((err) => (this.showAnalyticsMap = false))
      .finally(() =>
        this.setState({
          analyticsData: analytics,
          isAnalyticsDataLoading: false,
        })
      );
    axios
      .get(
        `${window._env_.BASE_URL}/gcris-api/researcher/${this.crisId}/profile`
      )
      .then((response) => {
        const body = response.data;
        body.educations.sort((a, b) =>
          a.startDate && b.startDate
            ? a.startDate.localeCompare(b.startDate)
            : 0
        );
        body.affiliations.sort((a, b) =>
          a.startDate && b.startDate
            ? a.startDate.localeCompare(b.startDate)
            : 0
        );
        body.awards.sort((a, b) =>
          a.date && b.date ? a.date.localeCompare(b.date) : 0
        );
        this.setState({
          publicationType: PUBLICATION_TYPES(locale)[0],
          imageURL: body.imageURL,
          cvURL: body.cvURL,
          title: body.fullName,
          mainAffiliation: body.mainAffiliation?.textvalue,
          variants: body.variants.join(", "),
          orcid: body.orcid,
          scopusAuthorId: body.scopusAuthorId,
          email: body.email,
          status: body.status,
          otherEmails: body.otherEmails.join(", "),
          researcherId: body.researcherId,
          googleScholar: body.googleScholarLink,
          affiliations: body.affiliations.map((e, i) => ({
            index: i + 1,
            ...e,
          })),
          educations: body.educations.map((e, i) => ({
            index: i + 1,
            ...e,
          })),
          biography: body.biography,
          biographyNative: body.biographyNative,
          yokLink: body.yokLink,
          websiteLink: body.websiteLink,
          websiteDesc: body.websiteDesc,
          researchInterests: body.researchInterests,
          awards: body.awards.map((e, i) => ({
            index: i + 1,
            ...e,
          })),
        });
      });

    // initial content load
    PUBLICATION_TYPES("tr").forEach((e) => this.updatePublication(e));
    this.updateTheses();
    this.updateProject();
  }

  onPublicationTypeChange = (publicationType) => {
    this.setState({ publicationType });
  };

  updatePublication = (publicationType) => {
    axios
      .get(
        `${window._env_.BASE_URL}/gcris-api/researcher/${this.crisId}/publications?type=${publicationType.value}`
      )
      .then((response) => response.data)
      .then((body) => {
        this.setState({
          publications: {
            ...this.state.publications,
            [publicationType.value]: body.map((e, i) => ({
              index: i + 1,
              ...e,
            })),
          },
        });
      });
  };

  updateTheses = () => {
    axios
      .get(
        `${window._env_.BASE_URL}/gcris-api/researcher/${this.crisId}/thesis`
      )
      .then((response) => response.data)
      .then((body) =>
        this.setState({
          theses: body.map((e, i) => ({ index: i + 1, ...e })),
        })
      );
  };

  updateProject = () => {
    axios
      .get(
        `${window._env_.BASE_URL}/gcris-api/researcher/${this.crisId}/project`
      )
      .then((response) => response.data)
      .then((body) =>
        this.setState({
          projects: body.map((e, i) => ({ index: i + 1, ...e })),
        })
      );
  };

  render() {
    const urlSearchParams = new URLSearchParams(this.props.location.search);
    const locale = urlSearchParams.get("locale") ?? "tr";
    const crisId = urlSearchParams.get("crisId");
    const uuid = urlSearchParams.get("uuid");
    const id = parseInt(crisId.slice(2), 10);
    const getStatus = (status) => {
      if (status === "0") {
        return t(locale, "/rs/profile/status/current");
      } else if (status === "1") {
        return t(locale, "/rs/profile/status/former");
      } else if (status === "2") {
        return t(locale, "/rs/profile/status/external");
      } else {
        return "";
      }
    };

    const publicationColumns = [
      {
        dataField: "index",
        text: "#",
        sort: true,
        headerStyle: { width: "60px" },
      },
      {
        dataField: "date",
        text: t(locale, "/rs/profile/publications/issueDate"),
        sort: true,
        headerStyle: { width: "150px" },
        formatter: (cell, row) => {
          if (cell) return cell.split("-")[0];
          return "";
        },
      },
      {
        dataField: "title",
        text: t(locale, "/rs/profile/publications/title"),
        sort: true,
        formatter: (cell, row) => {
          if (row.handle) {
            return (
              <a
                target="_blank"
                href={`${window._env_.BASE_URL}/handle/${row.handle}`}
              >
                {row.title}
              </a>
            );
          } else {
            return row.title;
          }
        },
      },
      {
        dataField: "authors",
        text: t(locale, "/rs/profile/publications/author"),
        sort: true,
        formatter: (cell, row) => {
          return cell.map((author, index) => {
            if (index == 3) {
              return (
                <div>
                  {t(locale, "/rs/profile/publications/etAll")}
                  <br />
                </div>
              );
            }
            if (index > 3) {
              return null;
            }
            if (author.crisId)
              return (
                <div>
                  <a
                    key={author.crisId}
                    target="_blank"
                    href={`${window._env_.BASE_URL}/cris/rp/${author.crisId}/information.html`}
                  >
                    {` ${author.name}; `}
                  </a>
                  <br />
                </div>
              );
            else
              return (
                <div>
                  {` ${author.name};`}
                  <br />
                </div>
              );
          });
        },
      },
      {
        dataField: "quartile",
        text: t(locale, "/rs/profile/publications/scopus-quartile"),
        sort: true,
        headerStyle: { width: "120px" },
      },
      {
        dataField: "scopus",
        text: t(locale, "/rs/profile/publications/scopus-cited"),
        sort: true,
        headerStyle: { width: "90px" },
      },
    ];

    const thesesColumns = [
      {
        dataField: "index",
        text: "#",
        sort: true,
        headerStyle: { width: "60px" },
      },
      {
        dataField: "date",
        text: t(locale, "/rs/profile/theses/issueDate"),
        sort: true,
        headerStyle: { width: "150px" },
      },
      {
        dataField: "title",
        text: t(locale, "/rs/profile/theses/title"),
        sort: true,
        formatter: (cell, row) => {
          if (row.handle) {
            return (
              <a
                target="_blank"
                href={`${window._env_.BASE_URL}/handle/${row.handle}`}
              >
                {row.title}
              </a>
            );
          } else {
            return row.title;
          }
        },
      },
      {
        dataField: "type",
        text: t(locale, "/rs/profile/theses/type"),
        sort: true,
        headerStyle: { width: "100px" },
        formatter: (cell, row) => {
          if (cell == "masterThesis") return "MSc";
          else if (cell == "doctoralThesis") return "PhD";
          else return cell;
        },
      },
      {
        dataField: "authors",
        text: t(locale, "/rs/profile/theses/author"),
        sort: true,
        formatter: (cell, row) => {
          return cell
            .map((author) => {
              if (author.crisId)
                return (
                  <a
                    key={author.crisId}
                    target="_blank"
                    href={`${window._env_.BASE_URL}/cris/rp/${author.crisId}/information.html`}
                  >
                    {author.name}
                  </a>
                );
              else if (author.name) return author.name;
            })
            .reduce(
              (acc, x, index) => (acc === null ? [x] : [acc, "; ", x]),
              null
            );
        },
      },
    ];

    const affiliationColumns = [
      {
        dataField: "index",
        text: "#",
        sort: true,
        headerStyle: { width: "60px" },
      },
      {
        dataField: "role",
        text: t(locale, "/rs/profile/affiliations/role"),
        sort: true,
      },
      {
        dataField: "organization",
        text: t(locale, "/rs/profile/affiliations/organization"),
        sort: true,
      },
      {
        dataField: "startDate",
        text: t(locale, "/rs/profile/affiliations/startDate"),
        sort: true,
        headerStyle: { width: "150px" },
        formatter: (cell, row) => {
          if (cell) return cell.split(" ")[0];
          return "";
        },
      },
      {
        dataField: "endDate",
        text: t(locale, "/rs/profile/affiliations/endDate"),
        sort: true,
        headerStyle: { width: "150px" },
        formatter: (cell, row) => {
          if (cell) return cell.split(" ")[0];
          return "";
        },
      },
    ];

    const educationColumns = [
      {
        dataField: "index",
        text: "#",
        sort: true,
        headerStyle: { width: "60px" },
      },
      {
        dataField: "program",
        text: t(locale, "/rs/profile/educations/program"),
        sort: true,
      },
      {
        dataField: "university",
        text: t(locale, "/rs/profile/educations/university"),
        sort: true,
      },
      {
        dataField: "startDate",
        text: t(locale, "/rs/profile/educations/startDate"),
        sort: true,
        headerStyle: { width: "150px" },
        formatter: (cell, row) => {
          if (cell) return cell.split(" ")[0];
          return "";
        },
      },
      {
        dataField: "endDate",
        text: t(locale, "/rs/profile/educations/endDate"),
        sort: true,
        headerStyle: { width: "150px" },
        formatter: (cell, row) => {
          if (cell) return cell.split(" ")[0];
          return "";
        },
      },
    ];
    const awardColumns = [
      {
        dataField: "index",
        text: "#",
        sort: true,
        headerStyle: { width: "60px" },
      },
      {
        dataField: "title",
        text: t(locale, "/rs/profile/awards/title"),
        sort: true,
        formatter: (cell, row) => {
          if (row.crisId) {
            return (
              <a
                target="_blank"
                href={`${window._env_.BASE_URL}/cris/awards/${row.crisId}`}
              >
                {row.title}
              </a>
            );
          } else {
            return row.title;
          }
        },
      },
      {
        dataField: "date",
        text: t(locale, "/rs/profile/awards/date"),
        sort: true,
        headerStyle: { width: "150px" },
        formatter: (cell, row) => {
          if (cell) return cell.split(" ")[0];
          return "";
        },
      },
    ];
    const researchInterestsColumns = [
      {
        dataField: "index",
        text: "#",
        sort: true,
        headerStyle: { width: "10px" },
        formatter: (cell, row, index) => {
          return index + 1;
        },
      },
      {
        dataField: "researchInterests",
        text: t(locale, "/rs/profile/tab/researchInterests"),
        sort: true,
        headerStyle: { width: "100px" },
        formatter: (cell, row) => {
          if (row) {
            return <p>{row.interest}</p>;
          }
        },
      },
    ];

    return (
      <div id="profile-container">
        <div
          className="only-print"
          style={{
            fontSize: 28,
            fontWeight: 700,
            color: "#001f54",
            paddingBottom: 24,
          }}
        >
          GCRIS
        </div>
        <Container fluid>
          {/* <Row>
            <Col>
              <ButtonGroup className="float-right mt-2">
                <Button variant="outline-dark" target="_blank" href={`${window._env_.BASE_URL}/cris/network/${this.crisId}`}><i className="fa fa-globe"></i> Network Lab</Button>
                <Button variant="outline-dark" target="_blank" href={`${window._env_.BASE_URL}/reports/rs?uuid=${uuid}&crisid=${this.crisId}`}><i className="fa fa-chart-bar"></i> View Statistics</Button>
                <Button variant="outline-dark" target="_blank" href={`${window._env_.BASE_URL}/cris/tools/subscription/subscribe?uuid=${uuid}`}><i className="fa fa-bell"></i> Email Alert</Button>
                <Button variant="outline-dark" target="_blank" href={`${window._env_.BASE_URL}/open-search?query=author_authority:${this.crisId}&format=rss`}><i className="fa fa-rss"></i> RSS Feed</Button>
              </ButtonGroup>
            </Col>
          </Row> */}
          <Row className="profile">
            <Col xs="12" sm="4" lg="3" className="profile-image-container">
              <div className="profile-image-wrapper">
                <img
                  src={
                    this.state.imageURL
                      ? window._env_.BASE_URL + this.state.imageURL
                      : profilePlaceholder
                  }
                  className="profile-image"
                />
              </div>
            </Col>
            <Col xs="12" sm="8" lg="9">
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "start",
                }}
              >
                <h1 className="researcher-title">{this.state.title}</h1>
                <PrintButton />
              </div>
              <table className="field-table">
                <tbody>
                  <tr>
                    <td className="field-label">
                      {t(locale, "/rs/profile/variants")}
                    </td>
                    <td className="field-value">{this.state.variants}</td>
                  </tr>
                  <tr>
                    <td className="field-label">
                      {t(locale, "/rs/profile/mainAffiliation")}
                    </td>
                    <td className="field-value">
                      {this.state.mainAffiliation}
                    </td>
                  </tr>
                  <tr>
                    <td className="field-label">
                      {t(locale, "/rs/profile/email")}
                    </td>
                    <td className="field-value">{this.state.email}</td>
                  </tr>
                  <tr>
                    <td className="field-label">
                      {t(locale, "/rs/profile/otherEmails")}
                    </td>
                    <td className="field-value">{this.state.otherEmails}</td>
                  </tr>
                  <tr>
                    <td className="field-label">
                      {t(locale, "/rs/profile/orcid")}
                    </td>
                    <td className="field-value">
                      <a
                        target="_blank"
                        href={`https://orcid.org/${this.state.orcid}`}
                      >
                        {this.state.orcid}
                      </a>
                    </td>
                  </tr>
                  <tr>
                    <td className="field-label">
                      {t(locale, "/rs/profile/scopusAuthorId")}
                    </td>
                    <td className="field-value">
                      <a
                        target="_blank"
                        href={`https://www.scopus.com/authid/detail.uri?authorId=${this.state.scopusAuthorId}`}
                      >
                        {this.state.scopusAuthorId}
                      </a>
                    </td>
                  </tr>
                  <tr>
                    <td className="field-label">
                      {t(locale, "/rs/profile/wosResearcherId")}
                    </td>
                    <td className="field-value">
                      <a
                        target="_blank"
                        href={`https://researcherid.com/rid/${this.state.researcherId}`}
                      >
                        {this.state.researcherId}
                      </a>
                    </td>
                  </tr>
                  <tr>
                    <td className="field-label">
                      {t(locale, "/rs/profile/googleScholar")}
                    </td>
                    <td className="field-value">
                      <a target="_blank" href={this.state.googleScholar}>
                        Google Scholar
                      </a>
                    </td>
                  </tr>
                  <tr>
                    <td className="field-label">
                      {t(locale, "/rs/profile/yokLink")}
                    </td>
                    <td className="field-value">
                      {this.state.yokLink && (
                        <a target="_blank" href={this.state.yokLink}>
                          {t(locale, "/rs/profile/yokLink")}
                        </a>
                      )}
                    </td>
                  </tr>
                  <tr>
                    <td className="field-label">
                      {t(locale, "/rs/profile/website")}
                    </td>
                    <td className="field-value">
                      {this.state.websiteLink && (
                        <a target="_blank" href={this.state.websiteLink}>
                          {this.state.websiteDesc}
                        </a>
                      )}
                    </td>
                  </tr>
                  <tr>
                    <td className="field-label">
                      {t(locale, "/rs/profile/documents")}
                    </td>
                    <td className="field-value">
                      {this.state.cvURL && (
                        <a
                          target="_blank"
                          href={window._env_.BASE_URL + this.state.cvURL}
                        >
                          CV
                        </a>
                      )}
                    </td>
                  </tr>
                  <tr>
                    <td className="field-label">
                      {t(locale, "/rs/profile/status")}
                    </td>
                    <td className="field-value">
                      {getStatus(this.state.status)}
                    </td>
                  </tr>
                </tbody>
              </table>
            </Col>
          </Row>
          {window._env_.DISPLAY_SDG_VIEW === "true" && (
            <DashboardSdgRosettes crisId={this.crisId} />
          )}

          <DashboardExternalScopus
            crisId={this.crisId}
            scopusAuthorId={this.state.scopusAuthorId}
          />

          <DashboardExternalWos crisId={this.crisId} />

          {window._env_.DISPLAY_SEMANTIC_SCHOLAR === "true" && (
            <DashboardExternalSemanticScholar crisId={this.crisId} />
          )}
          <DashboardExternalScholar
            crisId={this.crisId}
            googleScholar={this.state.googleScholar}
          />
          {window._env_.DISPLAY_OPEN_ALEX === "true" && (
            <DashboardExternalOpenAlex crisId={this.crisId} />
          )}

          <div className="page-break"></div>
          <DashboardSummary crisId={this.crisId} />

          {this.showAnalyticsMap &&
            (this.state.isAnalyticsDataLoading ? (
              <div className="d-flex align-items-center mb-4">
                <Spinner
                  style={{ width: "3rem", height: "3rem" }}
                  className="mr-2 text-large"
                  animation="border"
                  role="status"
                />
                <span style={{ fontSize: "18px", fontWeight: "normal" }}>
                  {t(
                    locale,
                    "/rs/profile/google-analytics/visitor-traffic-is-loading"
                  )}
                </span>
              </div>
            ) : (
              <DashboardGoogleAnalyticsMap
                analyticsData={this.state.analyticsData}
              />
            ))}
          <Row></Row>

          <Row className="tab-section">
            <Col>
              <Tab.Container
                id="left-tabs-example"
                defaultActiveKey="publications"
              >
                <Row>
                  <Col sm={3} style={{ marginBottom: "24px" }}>
                    <ListGroup>
                      <ListGroup.Item>
                        <h5>
                          {t(locale, `/institution/${window._env_.ENV_NAME}`) +
                            t(locale, "/rs/profile/tab-title/affiliated")}
                        </h5>
                        <Nav variant="pills" className="flex-column">
                          <Nav.Item>
                            <Nav.Link eventKey="publications">
                              {t(locale, "/rs/profile/tab/publications")}
                            </Nav.Link>
                          </Nav.Item>
                          <Nav.Item>
                            <Nav.Link eventKey="theses">
                              {t(locale, "/rs/profile/tab/theses")}
                            </Nav.Link>
                          </Nav.Item>
                          <Nav.Item>
                            <Nav.Link eventKey="projects">
                              {t(locale, "/rs/profile/tab/projects")}
                            </Nav.Link>
                          </Nav.Item>
                          <Nav.Item>
                            <Nav.Link eventKey="competencyCloud">
                              {t(locale, "/rs/profile/tab/competencyCloud")}
                            </Nav.Link>
                          </Nav.Item>
                        </Nav>
                      </ListGroup.Item>

                      <ListGroup.Item>
                        <h5>
                          {t(locale, "/rs/profile/tab-title/about-researcher")}
                        </h5>
                        <Nav.Item>
                          <a
                            className="nav-link font-italic"
                            target="_blank"
                            rel="noopener noreferrer"
                            href={`https://orcid.org/${this.state.orcid}`}
                          >
                            {t(locale, "/rs/profile/all-publications-orcid")}
                          </a>
                        </Nav.Item>
                        <Nav variant="pills" className="flex-column">
                          <Nav.Item>
                            <Nav.Link eventKey="affiliations">
                              {t(locale, "/rs/profile/tab/affiliations")}
                            </Nav.Link>
                          </Nav.Item>
                          {window._env_
                            .DISPLAY_BIOGRAPHY_AND_EDUCATIONS_LINKS ===
                            "true" && (
                            <>
                              <Nav.Item>
                                <Nav.Link eventKey="educations">
                                  {t(locale, "/rs/profile/tab/educations")}
                                </Nav.Link>
                              </Nav.Item>
                              <Nav.Item>
                                <Nav.Link eventKey="biography">
                                  {t(locale, "/rs/profile/tab/biography")}
                                </Nav.Link>
                              </Nav.Item>
                            </>
                          )}
                          <Nav.Item>
                            <Nav.Link eventKey="researchInterests">
                              {t(locale, "/rs/profile/tab/researchInterests")}
                            </Nav.Link>
                          </Nav.Item>
                          <Nav.Item>
                            <Nav.Link eventKey="awards">
                              {t(locale, "/rs/profile/tab/awards")}
                            </Nav.Link>
                          </Nav.Item>
                        </Nav>
                      </ListGroup.Item>
                    </ListGroup>
                  </Col>
                  <Col sm={9}>
                    <Tab.Content>
                      <Tab.Pane eventKey="publications">
                        <Row>
                          <Col>
                            <ButtonGroup className="publication-filter float-right mt-2">
                              {PUBLICATION_TYPES(locale).map((type) => (
                                <Button
                                  key={type.value}
                                  onClick={() =>
                                    this.onPublicationTypeChange(type)
                                  }
                                  active={this.state.publicationType === type}
                                >
                                  {type.text}
                                  <br />(
                                  {this.state.publications[type.value].length})
                                </Button>
                              ))}
                            </ButtonGroup>
                          </Col>
                        </Row>
                        <Row>
                          <Col>
                            <div className="wall">
                              <h5>
                                {t(locale, "/rs/profile/tab/publications")}
                              </h5>
                              <BootstrapTable
                                bootstrap4
                                striped
                                wrapperClasses="table-responsive"
                                keyField="index"
                                defaultSorted={[
                                  {
                                    dataField: "index",
                                    order: "asc",
                                  },
                                ]}
                                data={
                                  this.state.publications[
                                    this.state.publicationType.value
                                  ]
                                }
                                columns={publicationColumns}
                                pagination={paginationFactory(options)}
                              />
                            </div>
                          </Col>
                        </Row>
                      </Tab.Pane>
                      <Tab.Pane eventKey="theses">
                        <Row>
                          <Col>
                            <div className="wall">
                              <h5>{t(locale, "/rs/profile/tab/theses")}</h5>
                              <BootstrapTable
                                bootstrap4
                                striped
                                wrapperClasses="table-responsive"
                                keyField="index"
                                defaultSorted={[
                                  {
                                    dataField: "index",
                                    order: "asc",
                                  },
                                ]}
                                data={this.state.theses}
                                columns={thesesColumns}
                                pagination={paginationFactory(options)}
                              />
                            </div>
                          </Col>
                        </Row>
                      </Tab.Pane>
                      <Tab.Pane eventKey="projects">
                        <Row>
                          <Col>
                            <div className="wall">
                              <h5>{t(locale, "/rs/profile/tab/projects")}</h5>
                              {this.state.projects.map((project, i) => (
                                <div
                                  key={i}
                                  style={{
                                    padding: 16,
                                    border: "#eee 1px solid",
                                    borderRadius: 6,
                                    marginBottom: 16,
                                  }}
                                >
                                  <p style={{ fontWeight: 700 }}>
                                    {project.title}
                                  </p>
                                  <div
                                    style={{
                                      overflowX: "scroll",
                                    }}
                                  >
                                    <table style={{ width: "100%" }}>
                                      <thead>
                                        <tr>
                                          <th>
                                            {t(
                                              locale,
                                              "/rs/profile/projects/id"
                                            )}
                                          </th>
                                          <th>
                                            {t(
                                              locale,
                                              "/rs/profile/projects/program"
                                            )}
                                          </th>
                                          <th>
                                            {t(
                                              locale,
                                              "/rs/profile/projects/grantNo"
                                            )}
                                          </th>
                                          <th>
                                            {t(
                                              locale,
                                              "/rs/profile/projects/duration"
                                            )}
                                          </th>
                                          <th>
                                            {t(
                                              locale,
                                              "/rs/profile/projects/startDate"
                                            )}
                                          </th>
                                          <th>
                                            {t(
                                              locale,
                                              "/rs/profile/projects/endDate"
                                            )}
                                          </th>
                                          <th>
                                            {t(
                                              locale,
                                              "/rs/profile/projects/fund"
                                            )}
                                          </th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        <tr>
                                          <td>{project.pid}</td>
                                          <td>{project.fundingProgram}</td>
                                          <td>{project.grantno}</td>
                                          <td>{project.duration}</td>
                                          <td>
                                            {project.startdate
                                              ? project.startdate.split("T")[0]
                                              : ""}
                                          </td>
                                          <td>
                                            {project.enddate
                                              ? project.enddate.split("T")[0]
                                              : ""}
                                          </td>
                                          <td>{`${project.amount} ${project.currency}`}</td>
                                        </tr>
                                      </tbody>
                                    </table>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </Col>
                        </Row>
                      </Tab.Pane>
                      <Tab.Pane eventKey="affiliations">
                        <Row>
                          <Col>
                            <div className="wall">
                              <h5>
                                {t(locale, "/rs/profile/tab/affiliations")}
                              </h5>
                              <BootstrapTable
                                bootstrap4
                                striped
                                wrapperClasses="table-responsive"
                                keyField="index"
                                defaultSorted={[
                                  {
                                    dataField: "index",
                                    order: "asc",
                                  },
                                ]}
                                data={this.state.affiliations}
                                columns={affiliationColumns}
                                pagination={paginationFactory(options)}
                              />
                            </div>
                          </Col>
                        </Row>
                      </Tab.Pane>
                      <Tab.Pane eventKey="educations">
                        <Row>
                          <Col>
                            <div className="wall">
                              <h5>{t(locale, "/rs/profile/tab/educations")}</h5>
                              <BootstrapTable
                                bootstrap4
                                striped
                                wrapperClasses="table-responsive"
                                keyField="index"
                                defaultSorted={[
                                  {
                                    dataField: "index",
                                    order: "asc",
                                  },
                                ]}
                                data={this.state.educations}
                                columns={educationColumns}
                                pagination={paginationFactory(options)}
                              />
                            </div>
                          </Col>
                        </Row>
                      </Tab.Pane>
                      <Tab.Pane eventKey="biography">
                        <Row>
                          <Col>
                            <div className="wall">
                              <h5>{t(locale, "/rs/profile/tab/biography")}</h5>
                              <p>{this.state.biography}</p>
                              {this.state.biographyNative && (
                                <h6>
                                  {t(locale, "/rs/profile/tab/biographyNative")}
                                </h6>
                              )}
                              <p>{this.state.biographyNative}</p>
                            </div>
                          </Col>
                        </Row>
                      </Tab.Pane>
                      <Tab.Pane eventKey="competencyCloud">
                        <Row>
                          <Col>
                            <div className="wall">
                              <h5>
                                {t(
                                  locale,
                                  `/institution/${window._env_.ENV_NAME}`
                                ) +
                                  t(
                                    locale,
                                    "/rs/profile/tab-title/affiliated"
                                  )}{" "}
                                {t(locale, "/rs/profile/tab/competencyCloud")}
                              </h5>
                              <img
                                src={`${window._env_.BASE_URL}/public/researcherWordClouds/${id}.jpg`}
                                style={{ width: "100%" }}
                              />
                            </div>
                          </Col>
                        </Row>
                      </Tab.Pane>
                      <Tab.Pane eventKey="researchInterests">
                        <Row>
                          <Col>
                            <div className="wall">
                              <h5>
                                {t(locale, "/rs/profile/tab/researchInterests")}
                              </h5>
                              <BootstrapTable
                                bootstrap4
                                striped
                                wrapperClasses="table-responsive"
                                keyField="index"
                                defaultSorted={[
                                  {
                                    dataField: "index",
                                    order: "asc",
                                  },
                                ]}
                                data={this.state.researchInterests}
                                columns={researchInterestsColumns}
                                pagination={paginationFactory(options)}
                              />
                            </div>
                          </Col>
                        </Row>
                      </Tab.Pane>
                      <Tab.Pane eventKey="awards">
                        <Row>
                          <Col>
                            <div className="wall">
                              <h5>{t(locale, "/rs/profile/tab/awards")}</h5>
                              <BootstrapTable
                                bootstrap4
                                striped
                                wrapperClasses="table-responsive"
                                keyField="index"
                                defaultSorted={[
                                  {
                                    dataField: "index",
                                    order: "asc",
                                  },
                                ]}
                                data={this.state.awards}
                                columns={awardColumns}
                                pagination={paginationFactory(options)}
                              />
                            </div>
                          </Col>
                        </Row>
                      </Tab.Pane>
                    </Tab.Content>
                  </Col>
                </Row>
              </Tab.Container>
            </Col>
          </Row>
        </Container>
        <div className="print-footer only-print">
          <span>{t(locale, "/rs/profile/print-footer")}</span>
          <span>{` | ${window._env_.BASE_URL}/cris/rp/${this.crisId}`}</span>
        </div>
      </div>
    );
  }
}
